import {
  Accordion,
  Alert,
  BootstrapSizes,
  Button,
  ButtonColors,
  Modal,
  TalentechColors,
} from "@talentech/components";
import { Suspense, useEffect, useState } from "react";
import { useEditor } from "@grapesjs/react";
import { Editor } from "grapesjs";
import { CustomCommands } from "@/shared/utils";
import React from "react";

const MonacoEditor = React.lazy(() => import("@monaco-editor/react"));

type EditorLanguage = "css" | "javascript";
interface AccordionItem {
  title: string;
  onChange: (value?: string) => void;
  value: string | undefined;
  language: EditorLanguage;
}

const CustomCssModal: React.FC = () => {
  const editor = useEditor();
  const [showModal, setShowModal] = useState(false);
  const [customCss, setCustomCss] = useState(
    editor.getCss({ keepUnusedStyles: true })
  );
  const [customJs, setCustomJs] = useState<string | undefined>("");
  const [openAccordion, setOpenAccordion] = useState<EditorLanguage>("css");

  useEffect(() => {
    showModal && setCustomCss(editor.getCss({ keepUnusedStyles: true }));
    showModal &&
      setCustomJs(
        editor.DataSources.get("custom-js")?.getRecord("raw-js")?.values()[2]
      );
  }, [showModal]);

  useEffect(() => {
    editor.Commands.add(CustomCommands.MODAL_CUSTOM_CSS_OPEN, () =>
      setShowModal(true)
    );
    editor.Commands.add(CustomCommands.MODAL_CUSTOM_CSS_CLOSE, () =>
      setShowModal(false)
    );
    editor.Commands.add(
      CustomCommands.MODAL_CUSTOM_CSS_SAVE,
      async (
        editor: Editor,
        _ed: any,
        { form, language }: { form: string; language: EditorLanguage }
      ) => {
        if (language === "css") {
          await editor.Css.clear();
          await editor.Css.addCollection(form, {
            extend: true,
            avoidUpdateStyle: false,
          });
        } else {
          const records = editor.DataSources.get("custom-js")?.getRecords();

          if (records) {
            await editor.DataSources.get("custom-js")
              ?.getRecord("raw-js")
              ?.set("value", form);
          } else {
            await editor.DataSources.add({
              id: "custom-js",
              records: [
                {
                  id: "raw-js",
                  value: form,
                },
              ],
            });
          }
        }
        await editor.store();
        setShowModal(false);
      }
    );
  }, []);

  const modalProps = {
    title: "Custom Code",
    size: BootstrapSizes.XLARGE,
    showModal,

    onClose: () => editor.runCommand(CustomCommands.MODAL_CUSTOM_CSS_CLOSE),
    buttons: [
      <Button
        onClick={() => editor.runCommand(CustomCommands.MODAL_CUSTOM_CSS_CLOSE)}
        label="Cancel"
        outline
        key={"cancel"}
      />,
      <Button
        onClick={async () => {
          await editor.runCommand(CustomCommands.MODAL_CUSTOM_CSS_SAVE, {
            form: openAccordion === "css" ? customCss : customJs,
            language: openAccordion,
          })
          editor.loadProjectData(editor.getProjectData());
        }
        }
        label="save"
        key={"save"}
        color={ButtonColors.PRIMARY}
      />,
    ],
  };

  const accordionItems: AccordionItem[] = [
    {
      title: "CSS",
      onChange: (value?: string) => setCustomCss(value),
      value: customCss,
      language: "css",
    },
    {
      title: "JavaScript",
      value: customJs,
      onChange: (value?: string) => setCustomJs(value),
      language: "javascript",
    },
  ];

  const tidyCSS = async () => {
    if (!editor || !customCss) return;
    await setCustomCss(
      editor.getCss({
        onlyMatched: true,
        keepUnusedStyles: false,
      })
    );
  };

  return (
    <Modal {...modalProps}>
      <Suspense fallback={<div>Loading...</div>}>
        <Accordion
          defaultOpen={["css"]}
          onToggle={(id) => setOpenAccordion(id as EditorLanguage)}
          accordionItems={accordionItems.map(({ title, ...item }) => ({
            id: title.toLowerCase(),
            title: title,
            content: (
              <>
                {item.language === "css" && (
                  <div style={{ marginBottom: "10px" }}>
                    <Button
                      label="Tidy CSS"
                      onClick={tidyCSS}
                      color={ButtonColors.SECONDARY}
                      title="Remove unused CSS selectors"
                    />
                  </div>
                )}
                {item.language === "javascript" && (
                  <Alert
                    color={TalentechColors.DANGER}
                    message="Embedding JavaScript directly in HTML can expose your website to serious security risks, including Cross-Site Scripting (XSS), code maintainability issues or performance degradation"
                  />
                )}
                <MonacoEditor
                  height="400px"
                  {...item}
                  keepCurrentModel={false}
                  theme="vs-dark"
                  onMount={(editor) => {
                    setTimeout(() => {
                      editor.getAction("editor.action.formatDocument").run();
                    }, 100);
                  }}
                />
              </>
            ),
          }))}
        />
      </Suspense>
    </Modal>
  );
};

export default CustomCssModal;
